import { useMemo, useState } from 'react';
import { X, LayoutTemplate, RefreshCw } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../lib/api';
import {
  useStartConversation,
  startConversationErrorMessage,
} from '../lib/useStartConversation';
import { Button, SearchableSelect, FilterChips } from './ui';
import type { SearchableOption } from './ui';
import { TemplateVarsDialog, needsTemplateVars } from './TemplateVarsDialog';
import { TemplateGalleryDialog } from './TemplateGalleryDialog';
import { templateDisplayName } from '../lib/templateDisplay';
import type { Channel, Contact, Template } from '../types';

/**
 * „Neue Nachricht" — proaktiv einen Kontakt anschreiben.
 *
 * Ablauf:
 *  1. Kontakt wählen (Such-Combobox über GET /api/contacts).
 *  2. Kanal wählen (Default WhatsApp).
 *  3. WhatsApp = Cold Outbound: GENEHMIGTE Vorlage erforderlich (Galerie-Dialog),
 *     Variablen ausfüllen. E-Mail: freier Text.
 *  4. POST /api/conversations → POST /:id/messages → Navigation.
 */
const CHANNELS: readonly { value: Channel; label: string }[] = [
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'EMAIL', label: 'E-Mail' },
] as const;

export function NewMessageModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { startConversation } = useStartConversation();
  const [contactId, setContactId] = useState<string | null>(null);
  // Gewählter Kontakt als Objekt festhalten (für CONTACT-Variablen-Vorbefüllung). NICHT aus
  // dem volatilen Suchergebnis ableiten — sonst geht er bei erneuter Suche verloren.
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [channel, setChannel] = useState<Channel>('WHATSAPP');
  const [contactQuery, setContactQuery] = useState('');
  const [template, setTemplate] = useState<Template | null>(null);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [varsDialogOpen, setVarsDialogOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);

  const { data: contacts } = useQuery({
    queryKey: ['contacts', contactQuery],
    queryFn: () =>
      api.get<Contact[]>(
        `/api/contacts${contactQuery ? `?q=${encodeURIComponent(contactQuery)}` : ''}`,
      ),
    enabled: open,
  });

  const { data: templates } = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<{ items: Template[] }>('/api/templates'),
    enabled: open && channel === 'WHATSAPP',
  });

  const contactOptions: SearchableOption[] = useMemo(
    () =>
      (contacts ?? []).map((c) => ({
        value: c.id,
        label: c.displayName,
        sublabel: c.phones[0] ?? c.emails[0] ?? undefined,
      })),
    [contacts],
  );

  const approvedTemplates = useMemo(
    () => (templates?.items ?? []).filter((t) => t.metaStatus === 'APPROVED'),
    [templates],
  );

  if (!open) return null;

  const requiresTemplate = channel === 'WHATSAPP';

  // Spiegelt die harten Vorbedingungen aus onSend: Kontakt nötig; WhatsApp braucht eine Vorlage,
  // E-Mail braucht Vorlage ODER Text. So ist der Senden-Button schon vor dem Klick deaktiviert,
  // statt erst per Fehler-Toast zu meckern.
  const canSend = Boolean(contactId) && (requiresTemplate ? Boolean(template) : Boolean(template) || text.trim().length > 0);

  function reset() {
    setContactId(null);
    setSelectedContact(null);
    setChannel('WHATSAPP');
    setContactQuery('');
    setTemplate(null);
    setText('');
    setVarsDialogOpen(false);
    setGalleryOpen(false);
  }

  function close() {
    reset();
    onClose();
  }

  /** Validiert die Eingabe und entscheidet: Vorlagen-Variablen-Dialog öffnen oder direkt senden. */
  function onSend() {
    if (!contactId) {
      toast.error('Bitte einen Kontakt wählen.');
      return;
    }
    if (requiresTemplate && !template) {
      toast.error('Für WhatsApp ist eine genehmigte Vorlage erforderlich.');
      return;
    }
    if (!requiresTemplate && !template && !text.trim()) {
      toast.error('Bitte eine Nachricht eingeben.');
      return;
    }
    if (template) {
      // Hat die Vorlage Platzhalter → erst Ausfüll-Dialog; sonst direkt.
      if (needsTemplateVars(template)) {
        setVarsDialogOpen(true);
        return;
      }
      void actuallySend({ vars: [] });
      return;
    }
    void actuallySend();
  }

  /**
   * Legt die Konversation an und sendet. `vars` = positionale Vorlagen-Variablen (echte Vorlage);
   * `freeformText` = frei bearbeiteter Volltext → wird als normale Nachricht OHNE Template-Attachment
   * gesendet (auch wenn eine Vorlage gewählt war).
   */
  async function actuallySend(opts?: { vars?: string[]; freeformText?: string }) {
    if (!contactId) return;
    const freeform = opts?.freeformText;
    const useTemplate = Boolean(template) && freeform === undefined;
    setSubmitting(true);
    try {
      const conv = await startConversation({ contactId, channel });
      const attachments =
        useTemplate && template
          ? [
              {
                type: 'template',
                templateName: template.name,
                language: template.language,
                variables: opts?.vars ?? [],
              },
            ]
          : [];
      const contentText = useTemplate ? undefined : (freeform ?? text).trim() || undefined;
      await api.post(`/api/conversations/${conv.id}/messages`, { contentText, attachments });
      toast.success('Nachricht gesendet.');
      close();
    } catch (err) {
      toast.error(startConversationErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-6 pt-20">
      <div className="w-full max-w-md rounded-xl bg-white shadow-brand-lg">
        <header className="flex items-center justify-between border-b border-kalkuel-border px-5 py-3">
          <h2 className="text-sm font-semibold text-kalkuel-primary">Neue Nachricht</h2>
          <button
            type="button"
            onClick={close}
            className="rounded-pill p-1 text-kalkuel-muted hover:bg-kalkuel-surface hover:text-kalkuel-primary"
            title="Schließen"
          >
            <X size={16} />
          </button>
        </header>

        <div className="space-y-4 p-5">
          <div>
            <div className="kalkuel-eyebrow mb-1.5">Kontakt</div>
            <SearchableSelect
              options={contactOptions}
              value={contactId}
              onChange={(id) => {
                setContactId(id);
                setSelectedContact((contacts ?? []).find((c) => c.id === id) ?? null);
              }}
              onClear={() => {
                setContactId(null);
                setSelectedContact(null);
              }}
              placeholder="Kontakt wählen…"
              searchPlaceholder="Name, Telefon oder E-Mail…"
              emptyText="Keine Kontakte gefunden."
            />
          </div>

          <div>
            <div className="kalkuel-eyebrow mb-1.5">Kanal</div>
            <FilterChips
              value={channel}
              options={CHANNELS}
              onChange={(v) => {
                setChannel(v);
                setTemplate(null);
              }}
              size="sm"
            />
          </div>

          {requiresTemplate ? (
            <div>
              <div className="kalkuel-eyebrow mb-1.5">Vorlage (erforderlich)</div>
              <div className="mb-2 rounded-md border border-kalkuel-accent/30 bg-kalkuel-accent-soft px-3 py-2 text-[11px] text-kalkuel-primary">
                Bei einer neuen WhatsApp-Konversation ist nur eine genehmigte Vorlage zulässig.
              </div>

              {template ? (
                <div className="rounded-md border border-kalkuel-border bg-kalkuel-surface p-3">
                  <div className="flex items-start justify-between gap-2">
                    <span className="truncate text-sm font-semibold text-kalkuel-primary">
                      {templateDisplayName(template)}
                    </span>
                    <div className="flex shrink-0 gap-1">
                      <button
                        type="button"
                        onClick={() => setGalleryOpen(true)}
                        title="Andere Vorlage wählen"
                        className="rounded-pill p-1.5 text-kalkuel-primary hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-kalkuel-accent/40"
                      >
                        <RefreshCw size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setTemplate(null)}
                        title="Vorlage entfernen"
                        className="rounded-pill p-1.5 text-kalkuel-muted hover:bg-white hover:text-kalkuel-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-kalkuel-accent/40"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 whitespace-pre-wrap text-xs text-kalkuel-muted">
                    {template.body}
                  </div>
                  {needsTemplateVars(template) && (
                    <div className="mt-2 text-[11px] text-kalkuel-accent2">
                      Variablen werden vor dem Senden abgefragt.
                    </div>
                  )}
                </div>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setGalleryOpen(true)}
                  className="w-full"
                >
                  <LayoutTemplate size={14} />
                  Vorlage wählen
                </Button>
              )}
            </div>
          ) : (
            <div>
              <div className="kalkuel-eyebrow mb-1.5">Nachricht</div>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Nachricht…"
                className="h-24 w-full resize-none rounded-md border border-kalkuel-border p-3 text-sm focus:border-kalkuel-accent focus:outline-none focus:ring-2 focus:ring-kalkuel-accent/30"
              />
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-kalkuel-border px-5 py-3">
          <Button variant="ghost" size="sm" onClick={close}>
            Abbrechen
          </Button>
          <Button size="sm" onClick={onSend} disabled={submitting || !canSend}>
            Senden
          </Button>
        </footer>
      </div>

      <TemplateGalleryDialog
        open={galleryOpen}
        templates={approvedTemplates}
        onClose={() => setGalleryOpen(false)}
        onPick={(t) => setTemplate(t)}
      />

      {template && (
        <TemplateVarsDialog
          template={template}
          contact={selectedContact}
          open={varsDialogOpen}
          onClose={() => setVarsDialogOpen(false)}
          onConfirm={(result) => {
            setVarsDialogOpen(false);
            if (result.mode === 'template') {
              void actuallySend({ vars: result.vars });
            } else {
              void actuallySend({ freeformText: result.text });
            }
          }}
        />
      )}
    </div>
  );
}
